import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Users, Server, ScrollText, LayoutDashboard, Plus, ShieldCheck, ShieldAlert, Pencil, Trash2, TrendingUp, Wallet, Gift, Percent, CreditCard, CalendarClock, FileClock } from "lucide-react";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { PageHeader, StatCard, SectionCard } from "@/components/ui-helpers";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend, ReferenceLine } from "recharts";
import { toast } from "sonner";
import CustomerManageDialog from "@/pages/admin/CustomerManageDialog";

// Display-only label for the account role (internal id stays "travel_agent").
const roleLabel = (r) => r === "travel_agent" ? "Seller" : r === "super_admin" ? "Super Admin" : r === "customer" ? "Customer" : (r || "");
const sourceLabel = (s) => {
  const map = { manual_order: ["Manual order", "bg-indigo-100 text-indigo-700"], manual_booking: ["Booking", "bg-violet-100 text-violet-700"], manual: ["Manual", "bg-slate-100 text-slate-600"] };
  const m = map[s];
  if (!m) return <span className="text-xs text-slate-400">Sign-up</span>;
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${m[1]}`}>{m[0]}</span>;
};

export function AdminDashboard() {
  const [d, setD] = useState(null);
  const [bh, setBh] = useState(null);
  const [rev, setRev] = useState(null);
  const [months, setMonths] = useState(6);
  const [trend, setTrend] = useState(null);
  const [legal, setLegal] = useState(null);
  const [reconsentOpen, setReconsentOpen] = useState(false);
  const [reconsentUsers, setReconsentUsers] = useState(null);
  const navigate = useNavigate();
  const [legalTrend, setLegalTrend] = useState(null);
  const [referral, setReferral] = useState(null);
  const [cronLog, setCronLog] = useState(null);
  const [emailFails, setEmailFails] = useState(null);
  const [emailTrend, setEmailTrend] = useState(null);
  const [openJob, setOpenJob] = useState(null);
  const [jobRuns, setJobRuns] = useState({});
  const [digestSched, setDigestSched] = useState(null);
  const saveDigestSched = async (day, hour) => {
    setDigestSched({ day, hour });
    try { await api.put("/cron/admin/ops-digest-schedule", { day, hour }); toast.success("Ops digest schedule updated"); }
    catch { toast.error("Could not update schedule"); }
  };
  const [legalEvents, setLegalEvents] = useState([]);
  const [trendDays, setTrendDays] = useState(30);
  const [cooldownDays, setCooldownDays] = useState(() => Number(localStorage.getItem("reconsent_cooldown")) || 3);
  const remindReconsent = async () => {
    try { const r = await api.post(`/admin/legal/reconsent-remind?cooldown_days=${cooldownDays}`); toast.success(`Reminder sent to ${r.data.reminded} user(s)`); }
    catch { toast.error("Could not send reminders"); }
  };
  const openReconsent = () => {
    setReconsentOpen(true); setReconsentUsers(null);
    api.get("/admin/legal/reconsent-users").then((r) => setReconsentUsers(r.data.users)).catch(() => setReconsentUsers({ error: true }));
  };
  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => setD({ counts: {} }));
    api.get("/admin/revenue-stats").then((r) => setRev(r.data)).catch(() => {});
    api.get("/admin/legal/health").then((r) => setLegal(r.data)).catch(() => setLegal({ pending_schedules: 0, drafts: 0, reconsent_pending: 0, error: true }));
    api.get("/admin/referral-insights").then((r) => setReferral(r.data)).catch(() => {});
    api.get("/admin/cron-log").then((r) => setCronLog(r.data)).catch(() => {});
    api.get("/cron/admin/ops-digest-schedule").then((r) => setDigestSched(r.data)).catch(() => {});
    api.get("/admin/email-failures").then((r) => setEmailFails(r.data.accounts)).catch(() => {});
    api.get("/admin/email-trend").then((r) => setEmailTrend(r.data.trend)).catch(() => {});
    api.get("/marketplace/admin/dashboard/summary").then((r) => setBh(r.data)).catch(() => {});
  }, []);
  useEffect(() => { api.get(`/admin/legal/health-trend?days=${trendDays}`).then((r) => { setLegalTrend(r.data.series); setLegalEvents(r.data.events || []); }).catch(() => {}); }, [trendDays]);
  useEffect(() => { api.get(`/admin/funnel-trend?months=${months}`).then((r) => setTrend(r.data.series)).catch(() => {}); }, [months]);
  const c = d?.counts || {};
  return (
    <div>
      <PageHeader title="Super Admin Dashboard" icon={LayoutDashboard} subtitle="Live platform metrics — actual data only." />
      {bh && (
        <button onClick={() => navigate("/admin/marketplace")} data-testid="backup-health-badge"
          className={`mb-4 flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition hover:shadow-sm ${bh.backup_restore_ok ? "border-emerald-200 bg-emerald-50" : bh.backup_last_at ? "border-rose-200 bg-rose-50" : "border-amber-200 bg-amber-50"}`}>
          <ShieldCheck className={`h-5 w-5 ${bh.backup_restore_ok ? "text-emerald-600" : bh.backup_last_at ? "text-rose-600" : "text-amber-600"}`} />
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-800">Nightly backups {bh.backup_last_at ? (bh.backup_restore_ok ? "healthy" : "— restore test FAILED") : "not run yet"}</p>
            <p className="text-xs text-slate-500">{bh.backup_last_at ? `Last backup ${new Date(bh.backup_last_at).toLocaleString()} · ${bh.backup_count} kept · restore test ${bh.backup_restore_ok ? "passed ✓" : "failed ✗"}` : "Run your first backup in Marketplace → Data Backups"}</p>
          </div>
          <span className="text-xs font-semibold text-slate-400">Manage →</span>
        </button>
      )}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <StatCard icon={Users} label="Customers" value={c.customers ?? 0} tint="blue" />
        <StatCard icon={Users} label="Sellers" value={c.agents ?? 0} tint="purple" />
        <StatCard icon={ScrollText} label="Records" value={c.records ?? 0} tint="amber" />
        <StatCard icon={LayoutDashboard} label="Plans" value={c.plans ?? 0} tint="sky" />
        <StatCard icon={ShieldCheck} label="Support Tickets" value={c.support_tickets ?? 0} tint="rose" />
      </div>

      {legal && (
        <SectionCard title="Legal & Compliance Health" icon={ShieldCheck} className="mt-5" testid="legal-health"
          action={<Link to="/admin/legal" data-testid="legal-health-manage" className="text-xs font-medium text-blue-600 hover:text-blue-700">Manage →</Link>}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatCard icon={CalendarClock} label="Pending Schedules" value={legal.pending_schedules ?? 0} tint="purple" onClick={() => navigate("/admin/legal")} testid="health-schedules" />
            <StatCard icon={FileClock} label="Drafts Awaiting Publish" value={legal.drafts ?? 0} tint="amber" onClick={() => navigate("/admin/legal")} testid="health-drafts" />
            <StatCard icon={ShieldAlert} label="Users Owing Re-consent" value={legal.reconsent_pending ?? 0} tint="rose" onClick={openReconsent} testid="health-reconsent" />
          </div>
          {legal.error
            ? <p className="mt-3 text-[11px] text-rose-500">Couldn't load live compliance figures right now — showing defaults.</p>
            : <p className="mt-3 text-[11px] text-slate-400">Live compliance signals — pending scheduled legal publishes, unpublished drafts, and users who must re-accept the latest terms.</p>}
          {legalTrend && legalTrend.length > 1 && (
            <div className="mt-3" data-testid="reconsent-trend">
              <div className="mb-1 flex items-center justify-between">
                <p className="text-[11px] font-medium text-slate-500">Users owing re-consent over time</p>
                <div className="flex gap-1" data-testid="trend-range">
                  {[7, 30, 90].map((dd) => (<button key={dd} onClick={() => setTrendDays(dd)} data-testid={`trend-range-${dd}`} className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${trendDays === dd ? "bg-blue-100 text-blue-600" : "text-slate-400 hover:bg-slate-100"}`}>{dd}d</button>))}
                  <button onClick={() => downloadFile(`/admin/legal/health-trend?days=${trendDays}&fmt=csv`, "reconsent_trend.csv").catch(() => toast.error("Could not export"))} data-testid="trend-export-csv" className="rounded-full px-2 py-0.5 text-[10px] font-medium text-emerald-600 hover:bg-emerald-50">CSV</button>
                </div>
              </div>
              <div className="h-20">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={legalTrend}><XAxis dataKey="date" hide /><YAxis hide domain={[0, "dataMax+1"]} /><Tooltip />{legalEvents.filter((e) => legalTrend.some((s) => s.date === e.date)).map((e) => <ReferenceLine key={e.date} x={e.date} stroke="#a855f7" strokeDasharray="3 3" label={{ value: "publish", fontSize: 8, fill: "#a855f7" }} />)}<Line type="monotone" dataKey="reconsent_pending" stroke="#f43f5e" strokeWidth={2} dot={false} /></LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          {legalTrend && legalTrend.length === 1 && <p className="mt-2 text-[11px] text-slate-400">Re-consent trend appears once more daily snapshots are collected.</p>}
        </SectionCard>
      )}

      <SectionCard title="Revenue & Conversion" className="mt-5" testid="revenue-stats">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          <StatCard icon={Wallet} label="Revenue Collected" value={`₹${(rev?.revenue_collected ?? 0).toLocaleString("en-IN")}`} tint="emerald" />
          <StatCard icon={TrendingUp} label="Trials Started" value={rev?.trials_started ?? 0} tint="blue" />
          <StatCard icon={Percent} label="Trial → Paid" value={`${rev?.conversion_rate ?? 0}%`} tint="purple" />
          <StatCard icon={Gift} label="Coupon Redemptions" value={rev?.coupon_redemptions ?? 0} tint="amber" />
          <StatCard icon={CreditCard} label="Active Subscriptions" value={rev?.active_subscriptions ?? 0} tint="sky" />
          <StatCard icon={CreditCard} label="Paid Subscriptions" value={rev?.paid_subscriptions ?? 0} tint="rose" />
        </div>
        <p className="mt-3 text-[11px] text-slate-400">Figures are computed from real subscriptions, verified payments and coupon usage — never estimated.</p>
        {rev && (
          <div className="mt-4 space-y-2" data-testid="conversion-funnel">
            {[{ label: "Trials Started", value: rev.trials_started, color: "bg-blue-500" },
              { label: "Converted to Paid", value: rev.converted, color: "bg-purple-500" },
              { label: "Active Paid Subscriptions", value: rev.paid_subscriptions, color: "bg-emerald-500" }].map((s) => {
              const max = Math.max(rev.trials_started, rev.converted, rev.paid_subscriptions, 1);
              return (
                <div key={s.label} className="flex items-center gap-3">
                  <span className="w-48 shrink-0 text-xs text-slate-500">{s.label}</span>
                  <div className="h-6 flex-1 rounded-full bg-slate-100">
                    <div className={`flex h-6 items-center justify-end rounded-full ${s.color} px-2 text-[11px] font-bold text-white`} style={{ width: `${Math.max(6, (s.value / max) * 100)}%` }}>{s.value}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      <SectionCard title="Conversion Trend" className="mt-5" testid="funnel-trend"
        action={
          <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
            <SelectTrigger className="h-9 w-auto rounded-full border-slate-200 bg-white px-3 text-xs text-slate-600" data-testid="funnel-range"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="3">Last 3 months</SelectItem>
              <SelectItem value="6">Last 6 months</SelectItem>
              <SelectItem value="12">Last 12 months</SelectItem>
            </SelectContent>
          </Select>
        }>
        {(trend || []).some((t) => t.trials > 0 || t.paid > 0) ? (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend}>
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} width={30} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="trials" name="Trials started" stroke="#2563EB" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="paid" name="Paid subscriptions" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : <p className="py-10 text-center text-sm text-slate-400">No trials or paid subscriptions yet in this range. The trend appears as real activity accrues.</p>}
      </SectionCard>

      {referral && (
        <SectionCard title="Referral Program" className="mt-5" testid="admin-referral-insights"
          action={<div className="flex items-center gap-2">
            <button onClick={async () => { try { const r = await api.post("/admin/referral/milestone-preview", {}); toast.success(`Preview sent to ${r.data.sent_to}`); } catch { toast.error("Could not send preview"); } }} data-testid="milestone-preview-btn" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-medium text-slate-600 hover:border-amber-300 hover:text-amber-600"><Gift className="h-3.5 w-3.5" /> Preview email</button>
            <button onClick={async () => { try { const r = await api.post("/admin/referral/nudge-preview", {}); toast.success(`Nudge preview sent to ${r.data.sent_to}`); } catch { toast.error("Could not send preview"); } }} data-testid="nudge-preview-btn" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-medium text-slate-600 hover:border-blue-300 hover:text-blue-600"><TrendingUp className="h-3.5 w-3.5" /> Preview nudge</button>
            <button onClick={() => downloadFile("/admin/referral-insights?fmt=csv", "referral_insights.csv").catch(() => toast.error("Could not export"))} data-testid="referral-export-csv" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-medium text-slate-600 hover:border-emerald-300 hover:text-emerald-600"><TrendingUp className="h-3.5 w-3.5" /> Export CSV</button>
          </div>}>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard icon={Wallet} label="Points Paid Out" value={(referral.total_points_paid ?? 0).toLocaleString("en-IN")} tint="purple" />
            <StatCard icon={Gift} label="Qualified Referrals" value={referral.total_qualified ?? 0} tint="emerald" />
            <StatCard icon={Gift} label="Season Champions" value={(referral.champions || []).length} tint="amber" />
            <StatCard icon={Wallet} label="Ambassador Bonuses" value={(referral.by_type?.ambassador_bonus ?? 0).toLocaleString("en-IN")} tint="rose" />
          </div>
          <p className="mt-3 text-xs text-slate-500" data-testid="referral-cpa">Cost per acquisition: <b className="text-slate-700">{referral.total_qualified ? Math.round(referral.total_points_paid / referral.total_qualified).toLocaleString("en-IN") : 0} pts</b> per successful referral (1 pt = ₹1).</p>
          <p className="mt-1 text-xs text-slate-500" data-testid="referral-reach">Program reach: <b className="text-slate-700">{referral.active_ambassadors ?? 0}</b> active Ambassador(s) of <b className="text-slate-700">{referral.total_customers ?? 0}</b> customers ({referral.total_customers ? Math.round((referral.active_ambassadors / referral.total_customers) * 100) : 0}%).</p>
          {(referral.champions || []).length > 0 && (
            <div className="mt-4 divide-y divide-slate-50">
              {referral.champions.map((c) => (
                <div key={c.month} className="flex items-center justify-between py-2 text-sm" data-testid="admin-champion-row">
                  <span className="w-24 shrink-0 font-semibold text-slate-600">{c.month}</span>
                  <span className="min-w-0 flex-1 truncate px-3 text-slate-500">{c.name}</span>
                  <span className="shrink-0 text-slate-600">{c.qualified} referrals · +{c.bonus} pts</span>
                </div>
              ))}
            </div>
          )}
          <p className="mt-3 text-[11px] text-slate-400">Points and champions are computed from the verified ledger and referral events — never estimated.</p>
          <ReferralSettings />
        </SectionCard>
      )}

      {cronLog && (
        <SectionCard title="Scheduled Jobs" icon={CalendarClock} className="mt-5" testid="admin-cron-log"
          action={<button onClick={async () => { try { const r = await api.post("/cron/admin/ops-digest-preview"); toast.success(`Digest sent to ${r.data.sent_to}`); } catch { toast.error("Could not send digest"); } }} data-testid="digest-preview-btn" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-medium text-slate-600 hover:border-blue-300 hover:text-blue-600"><CalendarClock className="h-3.5 w-3.5" /> Preview digest</button>}>
          <div className="divide-y divide-slate-50">
            {cronLog.jobs.map((j) => (
              <div key={j.job} data-testid="cron-job-row">
                <div className="flex items-center justify-between gap-3 py-2 text-sm">
                  <button onClick={() => { const nx = openJob === j.job ? null : j.job; setOpenJob(nx); if (nx && !jobRuns[j.job]) api.get(`/admin/cron-log/${j.job}`).then((r) => setJobRuns((p) => ({ ...p, [j.job]: r.data.runs }))).catch(() => {}); }} data-testid={`cron-job-toggle-${j.job}`} className="font-medium text-slate-700 hover:text-blue-600">{j.job}</button>
                  <div className="flex items-center gap-3">
                    <span className="text-slate-500">{j.last_run ? new Date(j.last_run).toLocaleString("en-GB") : "Never run"}{j.runs ? ` · ${j.runs} run${j.runs === 1 ? "" : "s"}` : ""}</span>
                    <button onClick={async () => { try { await api.post(`/cron/run/${j.job}`); toast.success(`${j.job} triggered`); setTimeout(() => api.get("/admin/cron-log").then((r) => setCronLog(r.data)).catch(() => {}), 4000); } catch { toast.error("Could not trigger job"); } }} data-testid={`cron-run-${j.job}`} className="shrink-0 rounded-full border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-blue-300 hover:text-blue-600">Run now</button>
                  </div>
                </div>
                {openJob === j.job && (
                  <div className="mb-2 ml-1 rounded-lg bg-slate-50 p-2" data-testid={`cron-history-${j.job}`}>
                    {(jobRuns[j.job] || []).length ? (jobRuns[j.job] || []).map((rn, i) => <p key={i} className="text-[11px] text-slate-500">{new Date(rn.at).toLocaleString("en-GB")}</p>) : <p className="text-[11px] text-slate-400">No runs recorded.</p>}
                  </div>
                )}
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-slate-500" data-testid="cron-emails">Emails (last 30 days): <b className="text-emerald-600">{cronLog.emails_sent_30d} sent</b>, <b className={cronLog.emails_failed_30d ? "text-rose-600" : "text-slate-600"}>{cronLog.emails_failed_30d ?? 0} failed</b>, <b className="text-amber-600">{cronLog.emails_suppressed_30d ?? 0} suppressed</b>{cronLog.by_kind?.length ? ` · top: ${cronLog.by_kind.slice(0, 3).map((k) => `${k.kind} (${k.sent})`).join(", ")}` : ""}</p>
          {digestSched && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-600" data-testid="ops-digest-schedule">
              <span>Ops digest sends every</span>
              <Select value={String(digestSched.day)} onValueChange={(v) => saveDigestSched(Number(v), digestSched.hour)}>
                <SelectTrigger className="h-8 w-auto rounded-lg border-slate-200 px-2 py-1 text-xs" data-testid="ops-digest-day"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
              <span>at</span>
              <Select value={String(digestSched.hour)} onValueChange={(v) => saveDigestSched(digestSched.day, Number(v))}>
                <SelectTrigger className="h-8 w-auto rounded-lg border-slate-200 px-2 py-1 text-xs" data-testid="ops-digest-hour"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }).map((_, h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>)}
                </SelectContent>
              </Select>
              <span>UTC</span>
            </div>
          )}
          {emailTrend?.length > 0 && (
            <div className="mt-3 h-32" data-testid="email-trend">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={emailTrend}>
                  <XAxis dataKey="week" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} width={26} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="sent" stroke="#10b981" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="failed" stroke="#f43f5e" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          {cronLog.recent_failures?.length > 0 && (
            <div className="mt-2 rounded-lg bg-rose-50/60 p-2.5" data-testid="cron-failures">
              <div className="mb-1 flex items-center justify-between">
                <p className="text-[11px] font-semibold text-rose-600">Recent undeliverable recipients</p>
                <button onClick={async () => { try { const r = await api.post("/admin/email-retry-all"); toast.success(`Retried ${r.data.attempted}: ${r.data.resent} ok, ${r.data.failed} failed`); } catch { toast.error("Could not retry"); } }} data-testid="retry-all-btn" className="rounded-full border border-rose-200 px-2 py-0.5 text-[11px] font-medium text-rose-600 hover:bg-rose-100">Retry all fixable</button>
              </div>
              {cronLog.recent_failures.map((f) => (
                <div key={f.log_id || `${f.to}-${f.at}`} className="flex items-center justify-between gap-2 text-[11px] text-slate-500" data-testid="cron-failure-row">
                  <span className="truncate">{f.to} — {String(f.at).slice(0, 10)}</span>
                  {f.retryable && <button onClick={async () => { try { await api.post("/admin/email-retry", { log_id: f.log_id }); toast.success(`Resent to ${f.to}`); } catch (e) { toast.error(e.response?.data?.detail || "Could not resend"); } }} data-testid="email-retry-btn" className="shrink-0 rounded-full border border-rose-200 px-2 py-0.5 font-medium text-rose-600 hover:bg-rose-100">Resend</button>}
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      )}

      {emailFails && emailFails.length > 0 && (
        <SectionCard title="Accounts with Delivery Problems" icon={ShieldAlert} className="mt-5" testid="admin-email-failures">
          <p className="mb-2 text-xs text-slate-400">Addresses that failed 2+ times in the last 30 days — reach out to these members to correct their email.</p>
          <div className="divide-y divide-slate-50">
            {emailFails.map((a) => (
              <div key={a.email} className="flex items-center justify-between gap-3 py-2 text-sm" data-testid="email-failure-row">
                <div className="min-w-0">
                  <p className="truncate font-medium text-slate-700">{a.email}</p>
                  <p className="text-xs text-slate-400">{a.is_user ? `${a.name || "Member"} · ${roleLabel(a.role)}` : "Not a registered account"} · last {String(a.last_at).slice(0, 10)}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {a.flagged && <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[10px] font-semibold text-amber-700" data-testid="needs-email-update">needs email update</span>}
                  <span className="rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-semibold text-rose-600">{a.failures} fails</span>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

          {cronLog?.recent_suppressed?.length > 0 && (
            <div className="mt-2 rounded-lg bg-amber-50/60 p-2.5" data-testid="cron-suppressed">
              <p className="mb-1 text-[11px] font-semibold text-amber-700">Recently suppressed (3+ bounces)</p>
              {cronLog.recent_suppressed.map((s) => <p key={`${s.to}-${s.at}`} className="truncate text-[11px] text-slate-500" data-testid="suppressed-row">{s.to} — {String(s.at).slice(0, 10)}</p>)}
            </div>
          )}

      <SectionCard title="Privacy & Least Privilege" className="mt-5">
        <p className="text-sm text-slate-500">Super Admin sees aggregate operational data by default. Private customer/agent content (Gmail, documents, AI conversations, internal margins) is restricted and any sensitive access is permission-controlled and audited.</p>
      </SectionCard>

      <Dialog open={reconsentOpen} onOpenChange={setReconsentOpen}>
        <DialogContent className="max-w-lg" data-testid="reconsent-dialog">
          <DialogHeader><DialogTitle>Users Owing Re-consent</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {reconsentUsers === null && <p className="py-8 text-center text-sm text-slate-400">Loading…</p>}
            {reconsentUsers?.error && <p className="py-8 text-center text-sm text-rose-500">Couldn't load the list right now. Please try again.</p>}
            {Array.isArray(reconsentUsers) && reconsentUsers.length === 0 && <p className="py-8 text-center text-sm text-slate-400">Everyone is up to date — no one owes re-consent.</p>}
            {Array.isArray(reconsentUsers) && reconsentUsers.map((u) => (
              <div key={u.id} className="flex items-center justify-between border-b border-slate-50 py-2 last:border-0" data-testid="reconsent-user-row">
                <div className="min-w-0"><p className="truncate text-sm font-medium text-slate-700">{u.name}</p><p className="truncate text-xs text-slate-400">{u.email} · {roleLabel(u.role)}</p></div>
                <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-500">on v{u.consent_version}</span>
              </div>
            ))}
          </div>
          {Array.isArray(reconsentUsers) && reconsentUsers.length > 0 && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-xs text-slate-500">Cooldown</span>
              <Select value={String(cooldownDays)} onValueChange={(v) => { const n = Number(v); setCooldownDays(n); localStorage.setItem("reconsent_cooldown", String(n)); }}>
                <SelectTrigger className="h-8 w-auto rounded-md border-slate-200 text-xs" data-testid="cooldown-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="1">1 day</SelectItem><SelectItem value="3">3 days</SelectItem><SelectItem value="7">7 days</SelectItem></SelectContent>
              </Select>
              <button onClick={remindReconsent} data-testid="reconsent-remind-btn" className="flex-1 rounded-xl brand-gradient-bg py-2.5 text-sm font-semibold text-white">Email a reminder to all {reconsentUsers.length}</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function AdminCustomers() {
  const [rows, setRows] = useState([]);
  const [manage, setManage] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [inviting, setInviting] = useState(false);
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/admin/customers").then((r) => { setRows(r.data.customers); setSel(new Set()); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const leads = rows.filter((u) => u.claimed === false);
  const shown = filter === "leads" ? leads
    : filter === "customers" ? rows.filter((u) => u.role === "customer")
    : filter === "sellers" ? rows.filter((u) => u.role === "travel_agent")
    : rows;
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAllLeads = () => setSel(new Set(leads.map((u) => u.id)));
  const invite = async () => {
    if (sel.size === 0) { toast.error("Select at least one customer to invite."); return; }
    setInviting(true);
    try { const r = await api.post("/admin/customers/invite", { user_ids: [...sel] }); toast.success(`Invited ${r.data.sent} of ${r.data.requested} — claim & verify emails sent`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setInviting(false); }
  };
  return (
    <div>
      <PageHeader title="Customers & Sellers" icon={Users} subtitle="Manage plans, manual activations and subscription invoices per member."
        actions={<div className="flex items-center gap-2">
          {leads.length > 0 && <button onClick={selectAllLeads} data-testid="select-all-leads" className="rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:border-blue-300">Select all leads ({leads.length})</button>}
          <button onClick={invite} disabled={inviting || sel.size === 0} data-testid="admin-invite-selected" className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Invite selected ({sel.size})</button>
        </div>} />
      <div className="mb-3 flex flex-wrap gap-1.5" data-testid="customer-filters">
        {[["all", "All"], ["leads", `Leads (${leads.length})`], ["customers", "Customers"], ["sellers", "Sellers"]].map(([k, lbl]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`customer-filter-${k}`}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold ${filter === k ? "bg-blue-600 text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{lbl}</button>
        ))}
      </div>
      <div className="card-soft overflow-x-auto p-2">
        <Table>
          <TableHeader><TableRow><TableHead className="w-8"></TableHead><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Source</TableHead><TableHead>Account</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {shown.map((u) => (
              <TableRow key={u.id} data-testid="admin-customer-row">
                <TableCell>{u.claimed === false && <input type="checkbox" checked={sel.has(u.id)} onChange={() => toggle(u.id)} data-testid={`invite-check-${u.id}`} />}</TableCell>
                <TableCell className="font-medium">{u.name}{u.source === "manual" && <span className="ml-1 rounded-full bg-indigo-100 px-1.5 py-0.5 text-[9px] font-bold text-indigo-700">MANUAL</span>}</TableCell>
                <TableCell className="text-slate-500">{u.email}</TableCell>
                <TableCell>{roleLabel(u.role)}</TableCell>
                <TableCell>{sourceLabel(u.source)}</TableCell>
                <TableCell>{u.claimed === false
                  ? <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-600" data-testid={`lead-${u.id}`}>Lead{u.claim_invited_at ? " · invited" : ""}</span>
                  : <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-600">Active{u.mobile_verified ? " · ✓ mobile" : ""}</span>}</TableCell>
                <TableCell><span className={`rounded-full px-2 py-0.5 text-xs capitalize ${u.status === "suspended" ? "bg-rose-50 text-rose-600" : "bg-emerald-50 text-emerald-600"}`} data-testid={`customer-status-${u.id}`}>{u.status || "active"}</span></TableCell>
                <TableCell className="text-right">
                  <button onClick={() => setManage(u)} data-testid={`manage-user-${u.id}`} className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-blue-300 hover:text-blue-600">Manage</button>
                </TableCell>
              </TableRow>
            ))}
            {shown.length === 0 && <TableRow><TableCell colSpan={8} className="py-8 text-center text-slate-400">No users match this filter.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      {manage && <CustomerManageDialog user={manage} onClose={() => setManage(null)} onUpdated={load} />}
    </div>
  );
}

export function AdminProviders() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "", url: "" });
  const [editId, setEditId] = useState(null);
  const load = () => api.get("/admin/providers").then((r) => setRows(r.data.providers)).catch(() => {});
  useEffect(() => { load(); }, []);
  const openNew = () => { setEditId(null); setForm({ name: "", category: "", url: "" }); setOpen(true); };
  const openEdit = (p) => { setEditId(p.provider_id); setForm({ name: p.name || "", category: p.category || "", url: p.url || "" }); setOpen(true); };
  const save = async () => {
    try {
      if (editId) { await api.patch(`/admin/providers/${editId}`, form); toast.success("Provider updated"); }
      else { await api.post("/admin/providers", form); toast.success("Provider added"); }
      setOpen(false); setEditId(null); setForm({ name: "", category: "", url: "" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete provider "${p.name}"?`)) return;
    try { await api.delete(`/admin/providers/${p.provider_id}`); toast.success("Provider deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <PageHeader title="Providers" icon={Server}
        actions={<button onClick={openNew} data-testid="admin-add-provider" className="inline-flex items-center gap-2 rounded-full brand-gradient-bg px-4 py-2.5 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> Add Provider</button>} />
      <div className="card-soft overflow-x-auto p-2">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead>URL</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((p) => (
              <TableRow key={p.provider_id}>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell className="capitalize">{p.category?.replace(/_/g, " ")}</TableCell>
                <TableCell className="text-slate-500">{p.url || "—"}</TableCell>
                <TableCell className="text-right">
                  <button onClick={() => openEdit(p)} data-testid={`edit-provider-${p.provider_id}`} className="rounded-lg p-1.5 text-slate-400 hover:bg-blue-50 hover:text-blue-500"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(p)} data-testid={`delete-provider-${p.provider_id}`} className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>{editId ? "Edit Provider" : "Add Provider"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="provider-name-input" className="mt-1" /></div>
            <div><Label className="text-xs">Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="provider-category-input" placeholder="flights / hotels / ..." className="mt-1" /></div>
            <div><Label className="text-xs">URL</Label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="provider-url-input" className="mt-1" /></div>
          </div>
          <button onClick={save} data-testid="provider-save-btn" className="mt-2 w-full rounded-xl brand-gradient-bg py-2.5 font-semibold text-white">{editId ? "Save" : "Add"}</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function AdminPlans() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/plans").then((r) => setRows(r.data.plans)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = {
        name: edit.name, price: Number(edit.price) || 0, currency: edit.currency || "INR",
        billing_cycle: edit.billing_cycle || "monthly",
        trial_days: Number(edit.trial_days) || 0, promo: edit.promo || "",
        regional_pricing: edit.regional_pricing || {}, entitlements: edit.entitlements || {},
      };
      if (edit.plan_id) { await api.patch(`/admin/plans/${edit.plan_id}`, payload); toast.success("Plan updated"); }
      else { await api.post("/admin/plans", { ...payload, tier: (edit.tier || edit.name || "custom").toLowerCase().replace(/\s+/g, "_"), features: edit.features || [] }); toast.success("Plan created"); }
      setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete plan "${p.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/plans/${p.plan_id}`); toast.success("Plan deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setRegion = (code, field, value) => {
    setEdit((s) => {
      const rp = { ...(s.regional_pricing || {}) };
      rp[code] = { ...(rp[code] || {}), [field]: field === "price" ? Number(value) : value };
      return { ...s, regional_pricing: rp };
    });
  };

  const setEnt = (k, v) => setEdit((s) => ({ ...s, entitlements: { ...(s.entitlements || {}), [k]: v } }));

  const REGIONS = ["US", "AE", "GB", "SG"];

  return (
    <div>
      <PageHeader title="Subscription Plans" icon={ScrollText} subtitle="Set per-region pricing, free-trial windows and promo offers. Prices are shown to signed-in users only."
        actions={<button onClick={() => setEdit({ name: "", tier: "", price: 0, currency: "INR", billing_cycle: "monthly", trial_days: 0, promo: "", regional_pricing: {}, entitlements: {}, features: [] })} data-testid="admin-new-plan" className="inline-flex items-center gap-2 rounded-full brand-gradient-bg px-4 py-2.5 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> New Plan</button>} />
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {rows.map((p) => (
          <div key={p.plan_id || p.name} className="card-soft p-5" data-testid={`admin-plan-${p.tier}`}>
            <div className="flex items-start justify-between">
              <h3 className="font-brand text-lg font-bold text-slate-800">{p.name}</h3>
              {p.plan_id && <button onClick={() => setEdit({ ...p, regional_pricing: p.regional_pricing || {}, entitlements: p.entitlements || {} })} data-testid={`edit-plan-${p.tier}`} className="rounded-lg p-1.5 text-slate-400 hover:bg-blue-50 hover:text-blue-500"><Pencil className="h-4 w-4" /></button>}
              {p.plan_id && <button onClick={() => del(p)} data-testid={`delete-plan-${p.tier}`} className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>}
            </div>
            <p className="mt-1 text-2xl font-bold text-blue-600">₹{p.price}<span className="text-sm font-normal text-slate-400">/mo</span></p>
            {p.promo && <span className="mt-1 inline-block rounded-full bg-purple-50 px-2 py-0.5 text-[10px] font-medium text-purple-600">{p.promo}</span>}
            {p.trial_days > 0 && <p className="mt-1 text-xs text-emerald-600">{p.trial_days}-day free trial</p>}
            <ul className="mt-3 space-y-1 text-sm text-slate-600">{(p.features || []).map((f) => <li key={f}>• {f}</li>)}</ul>
            <div className="mt-2 flex flex-wrap gap-1">
              {p.entitlements?.whatsapp_connect && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-600">WhatsApp connect</span>}
              {p.entitlements?.whatsapp_bulk && <span className="rounded-full bg-purple-50 px-2 py-0.5 text-[10px] font-medium text-purple-600">Bulk WhatsApp</span>}
            </div>
            {p.regional_pricing && Object.keys(p.regional_pricing).length > 0 && (
              <p className="mt-2 text-[11px] text-slate-400">Regional: {Object.entries(p.regional_pricing).map(([c, v]) => `${c} ${v.currency} ${v.price}`).join(" · ")}</p>
            )}
          </div>
        ))}
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="edit-plan-dialog">
          <DialogHeader><DialogTitle>Edit {edit?.name}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div><Label className="text-xs">Plan Name</Label><Input value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="plan-name-input" className="mt-1" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs">Base Price</Label><Input type="number" value={edit.price ?? 0} onChange={(e) => setEdit({ ...edit, price: e.target.value })} data-testid="plan-price-input" className="mt-1" /></div>
                <div><Label className="text-xs">Currency</Label><Input value={edit.currency || "INR"} onChange={(e) => setEdit({ ...edit, currency: e.target.value.toUpperCase() })} data-testid="plan-currency-input" className="mt-1" /></div>
                <div><Label className="text-xs">Billing</Label><select value={edit.billing_cycle || "monthly"} onChange={(e) => setEdit({ ...edit, billing_cycle: e.target.value })} data-testid="plan-billing-input" className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"><option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="yearly">Yearly</option></select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Free Trial (days)</Label><Input type="number" value={edit.trial_days ?? 0} onChange={(e) => setEdit({ ...edit, trial_days: e.target.value })} data-testid="plan-trial-input" className="mt-1" /></div>
                <div><Label className="text-xs">Promo Text</Label><Input value={edit.promo || ""} onChange={(e) => setEdit({ ...edit, promo: e.target.value })} data-testid="plan-promo-input" placeholder="14-day free trial" className="mt-1" /></div>
              </div>
              <div>
                <Label className="text-xs">Regional Pricing</Label>
                <div className="mt-1 space-y-2">
                  {REGIONS.map((c) => (
                    <div key={c} className="flex items-center gap-2">
                      <span className="w-8 text-xs font-semibold text-slate-500">{c}</span>
                      <Input placeholder="Currency (USD)" value={edit.regional_pricing?.[c]?.currency || ""} onChange={(e) => setRegion(c, "currency", e.target.value.toUpperCase())} data-testid={`plan-region-cur-${c}`} className="h-9" />
                      <Input type="number" placeholder="Price" value={edit.regional_pricing?.[c]?.price ?? ""} onChange={(e) => setRegion(c, "price", e.target.value)} data-testid={`plan-region-price-${c}`} className="h-9" />
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs">WhatsApp entitlements</Label>
                <div className="mt-1.5 space-y-2">
                  <label className="flex items-center justify-between rounded-lg border border-slate-100 p-2.5">
                    <span className="text-sm text-slate-700">WhatsApp connect <span className="text-xs text-slate-400">— sharing + single compose</span></span>
                    <input type="checkbox" checked={!!edit.entitlements?.whatsapp_connect} onChange={(e) => setEnt("whatsapp_connect", e.target.checked)} data-testid="plan-wa-connect" className="h-4 w-4" />
                  </label>
                  <label className="flex items-center justify-between rounded-lg border border-slate-100 p-2.5">
                    <span className="text-sm text-slate-700">Bulk WhatsApp <span className="text-xs text-slate-400">— marketing / paste numbers</span></span>
                    <input type="checkbox" checked={!!edit.entitlements?.whatsapp_bulk} onChange={(e) => setEnt("whatsapp_bulk", e.target.checked)} data-testid="plan-wa-bulk" className="h-4 w-4" />
                  </label>
                </div>
              </div>
            </div>
          )}
          <button onClick={save} data-testid="plan-save-btn" className="mt-2 w-full rounded-xl brand-gradient-bg py-2.5 font-semibold text-white">Save Plan</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReferralSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/referral-settings").then((r) => setS(r.data.settings)).catch(() => {}); }, []);
  const save = async () => {
    try {
      await api.put("/admin/referral-settings", { enabled: !!s.enabled, tier1_points: Number(s.tier1_points) || 0, tier2_points: Number(s.tier2_points) || 0, tier3_points: Number(s.tier3_points) || 0 });
      toast.success("Referral rules saved");
    } catch { toast.error("Could not save"); }
  };
  if (!s) return null;
  const f = (k, label) => (<div><Label className="text-xs">{label}</Label><Input type="number" value={s[k] ?? 0} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`referral-${k}`} className="mt-1" /></div>);
  return (
    <div className="mt-4 rounded-xl border border-slate-100 p-4" data-testid="referral-settings">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-slate-700">Referral Rules</h4>
        <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={!!s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} data-testid="referral-enabled" className="h-4 w-4" /> Program enabled</label>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {f("tier1_points", "Tier 1 pts (1–2)")}
        {f("tier2_points", "Tier 2 pts (3–5)")}
        {f("tier3_points", "Tier 3 pts (6+)")}
      </div>
      <button onClick={save} data-testid="referral-settings-save" className="mt-3 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white">Save rules</button>
    </div>
  );
}

export function AdminAudit() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/audit").then((r) => setRows(r.data.logs)).catch(() => {}); }, []);
  return (
    <div>
      <PageHeader title="Audit Logs" icon={ShieldCheck} subtitle="Sensitive operations are recorded. Secrets are never logged." />
      <div className="card-soft overflow-x-auto p-2">
        <Table>
          <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>Actor</TableHead><TableHead>Action</TableHead><TableHead>Target</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((l, i) => (
              <TableRow key={i}><TableCell className="text-xs text-slate-500">{new Date(l.created_at).toLocaleString("en-GB")}</TableCell><TableCell>{l.actor_email}</TableCell><TableCell className="font-medium">{l.action}</TableCell><TableCell className="text-slate-500">{l.target}</TableCell></TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={4} className="py-8 text-center text-slate-400">No audit entries yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
